import { useCallback, useEffect, useState } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'lensa.theme';
const ORDER: ThemeChoice[] = ['light', 'dark', 'system'];

function readStored(): ThemeChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

/**
 * §G7 poin 2: saat OS tidak menyatakan preferensi, `system` diarahkan ke gelap
 * — bukan ke terang. Ruang monitor CCTV adalah konteks pemakaian utamanya.
 * Karena itu yang ditanya adalah "apakah OS minta terang", bukan sebaliknya.
 */
function resolveDark(choice: ThemeChoice): boolean {
  if (choice === 'dark') return true;
  if (choice === 'light') return false;
  return !window.matchMedia('(prefers-color-scheme: light)').matches;
}

export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(readStored);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', resolveDark(choice));
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      /* penyimpanan diblokir — tema tetap jalan untuk sesi ini */
    }
    // TODO: sinkronkan ke users.theme lewat PUT /api/v1/auth/me (§G6) begitu
    // endpoint auth ada, supaya pilihan ikut berpindah antar perangkat.
  }, [choice]);

  // Saat memilih 'system', ikuti perubahan preferensi OS tanpa reload.
  useEffect(() => {
    if (choice !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => document.documentElement.classList.toggle('dark', resolveDark('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [choice]);

  const cycle = useCallback(() => {
    setChoice((c) => ORDER[(ORDER.indexOf(c) + 1) % ORDER.length]!);
  }, []);

  return { choice, setChoice, cycle };
}
